/* ==========================================================================
   WAYPOINT — shared bottom-sheet hygiene, extracted from the original "New
   Trip" sheet implementation so every modal sheet in the app (New Trip,
   Account, Journal's trip switcher, ...) gets the same behavior instead of
   drifting: Escape closes, body scroll locks while open, focus starts
   inside the sheet and is trapped there, focus restores to whatever opened
   the sheet on close, drag-to-dismiss uses the spec's exact release
   physics, and everything collapses to a plain opacity fade (no slide, no
   drag) under prefers-reduced-motion.
   ========================================================================== */

import { useEffect, useRef } from 'react';
import { useDragControls, useReducedMotion, type PanInfo } from 'framer-motion';

export const SHEET_SPRING = { type: 'spring' as const, stiffness: 420, damping: 38, mass: 0.9 };
const DISMISS_THRESHOLD_PX = 120;
const VELOCITY_PROJECTION = 0.18;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Drop onto the sheet's root `motion.div` via spread: `<motion.div
 * ref={sheetRef} {...sheetMotion}>`. */
export function useModalSheet(onClose: () => void) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);
  const reduceMotion = useReducedMotion();
  const dragControls = useDragControls();

  useEffect(() => {
    openerRef.current = document.activeElement;
    const firstFocusable = sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    // preventScroll matters on a sheet that scrolls: focus() otherwise scrolls the target into
    // view, and in a sheet whose first focusable sits BELOW the fold (the Insights sheet's only
    // control is its "Done" button, at the bottom) that opens the drawer already scrolled past
    // its own heading. Focus still lands correctly for the keyboard and the trap below; it just
    // no longer drags the scroll container with it.
    firstFocusable?.focus({ preventScroll: true });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const focusable = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleDragEnd(_: unknown, info: PanInfo) {
    const projected = info.offset.y + info.velocity.y * VELOCITY_PROJECTION;
    if (projected > DISMISS_THRESHOLD_PX) onClose();
  }

  const sheetMotion = {
    initial: reduceMotion ? { opacity: 0 } : { y: '100%' },
    animate: reduceMotion ? { opacity: 1 } : { y: 0 },
    exit: reduceMotion ? { opacity: 0 } : { y: '100%' },
    transition: reduceMotion ? { duration: 0.15 } : SHEET_SPRING,
    drag: (reduceMotion ? false : 'y') as 'y' | false,
    dragListener: false as const,
    dragControls,
    dragConstraints: { top: 0, bottom: 0 },
    dragElastic: { top: 0, bottom: 0.4 },
    onDragEnd: handleDragEnd,
  };

  return { sheetRef, reduceMotion, dragControls, sheetMotion };
}

/** The 36×4px visual grabber inside a 44px hit area, wired to the sheet's
 * dragControls so pressing anywhere in the hit area (not just the thin bar)
 * starts the drag. */
export function SheetGrabber({ dragControls }: { dragControls: ReturnType<typeof useDragControls> }) {
  return (
    <div className="wp-sheet-grabber-hit" onPointerDown={(e) => dragControls.start(e)}>
      <div className="wp-sheet-grabber" aria-hidden="true" />
    </div>
  );
}

export const SHEET_GRABBER_CSS = `
  .wp-sheet-grabber-hit {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    margin: -8px 0 -8px;
    touch-action: none;
  }
  .wp-sheet-grabber {
    width: 36px; height: 4px;
    border-radius: var(--radius-pill);
    background: var(--cloud);
  }
`;
